import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingCircle from 'components/ui/LoadingCircle';
import Pagination from 'components/v2/PaginationV2';
import { showToast } from 'stores/ToastStore';
import { getFileIconClass } from 'helpers/helper_functions';
import { BARCODE } from 'config/documents';
import { useGetDocuments, useGetSignedUrls } from 'utils/DocumentsHooks';

const DOCUMENTS_PER_PAGE = 10;

const getAllForeignKeysForShipment = (shipment) => {
  const invoiceIds =
    shipment.receivables?.map((receivable) => receivable.invoice_id) || [];

  const payoutIds =
    shipment.payables?.map((payable) => payable.payout_id) || [];

  return [shipment._id, ...invoiceIds, ...payoutIds].filter((key) => key);
};

export const ShipmentDocumentList = ({ shipments }) => {
  const [page, setPage] = useState(1);

  const { t } = useTranslation();
  const pageCount = Math.ceil(
    (shipmentDocumentsData?.count || 0) / DOCUMENTS_PER_PAGE,
  );
  const shipmentDocumentForeignKeys = [
    ...new Set(
      shipments
        .map((shipment) => getAllForeignKeysForShipment(shipment))
        .flat()
        .filter((key) => key),
    ),
  ];

  const {
    data: shipmentDocumentsData,
    error: shipmentDocumentsError,
    loading,
  } = useGetDocuments({
    foreignKeys: shipmentDocumentForeignKeys,
    options: {
      limit: DOCUMENTS_PER_PAGE,
      skip: (page - 1) * DOCUMENTS_PER_PAGE,
      order: [{ field: 'createdAt', direction: 'desc' }],
    },
  });
  const { data: signedUrlsData, error: signedUrlsError } = useGetSignedUrls({
    foreignKeys: shipmentDocumentForeignKeys,
  });

  const openSignedUrl = (document) => async (event) => {
    event.preventDefault();

    const signedUrlData = signedUrlsData?.signedUrls.find(
      (data) => data.id === document.id,
    );

    window.open(signedUrlData?.signedUrl, 'new');
  };

  useEffect(() => {
    if (shipmentDocumentsError) {
      showToast(
        t('GET_DOCUMENT_ERROR'),
        t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: shipmentDocumentsError }),
        'error',
      );
    }
  }, [shipmentDocumentsError]);

  useEffect(() => {
    if (signedUrlsError) {
      showToast(
        t('GET_SIGNED_URL_ERROR'),
        t('GET_SIGNED_URL_FAILED_WITH_ERROR', {
          error: signedUrlsError,
        }),
        'error',
      );
    }
  }, [signedUrlsError]);

  const renderShipmentDocuments = (shipment) => {
    const foreignKeys = getAllForeignKeysForShipment(shipment);

    return shipmentDocumentsData?.documents
      .filter(
        (document) =>
          [document.foreignKeys, foreignKeys].reduce((a, b) =>
            a.filter((c) => b.includes(c)),
          ).length,
      )
      .filter((document) => document.type !== BARCODE.value)
      .map((document) => (
        <tr key={`shipment-document-${document.key}`}>
          <td>
            <div className="flex items-center space-x-2">
              <i
                className={getFileIconClass(
                  document.key.split('.').pop().toUpperCase(),
                )}
              />
              <a
                href="#"
                title={document.name}
                onClick={openSignedUrl(document)}
              >
                {document.name}
              </a>
            </div>
          </td>
          <td>
            <a href={`/shipments/${shipment._id}`} target="blank">
              {shipment.shipment_id}
            </a>
          </td>
        </tr>
      ));
  };

  if (!shipments.length) {
    return <i>{t('NO_SHIPMENT_ATTACHED_TO_ORDER')}</i>;
  }

  return (
    <>
      {loading ? (
        <LoadingCircle text={t('LOADING')} />
      ) : (
        <table className="default-table">
          <thead>
            <tr>
              <th>{t('DOCUMENT', { count: 1 })}</th>
              <th>{t('SHIPMENT', { count: 1 })}</th>
            </tr>
          </thead>
          <tbody>
            {shipments.map((shipment) => renderShipmentDocuments(shipment))}
          </tbody>
        </table>
      )}
      <Pagination
        className="border-t-0"
        perPage={DOCUMENTS_PER_PAGE}
        page={page}
        pages={pageCount}
        total={shipmentDocumentsData?.count}
        showCount
        onPageClick={(page) => setPage(page)}
        pageNeighbours={0}
      />
    </>
  );
};
